import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type { AdminRoster, NamedRef } from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

const strings = appStrings.modules.rostersAdmin;

export const ROSTERS_ADMIN_TAB = {
  OPERATORS: 'operators',
  COHORTS: 'cohorts',
} as const;

export type RostersAdminTab = (typeof ROSTERS_ADMIN_TAB)[keyof typeof ROSTERS_ADMIN_TAB];

export const ROSTERS_ADMIN_TABS: readonly RostersAdminTab[] = [ROSTERS_ADMIN_TAB.COHORTS];

export const DEFAULT_ROSTERS_ADMIN_TAB: RostersAdminTab = ROSTERS_ADMIN_TAB.COHORTS;

export const ROSTERS_ADMIN_TAB_LABELS: Record<RostersAdminTab, string> = {
  [ROSTERS_ADMIN_TAB.OPERATORS]: strings.tabs.operators,
  [ROSTERS_ADMIN_TAB.COHORTS]: strings.tabs.cohorts,
};

/** With fleet access, platform mode offers no cohorts tab, since it manages none — which also
 * makes it unreachable by URL, because a tab is parsed against this list. Without fleet access
 * the ordinary tabs answer whatever `platformMode` says: the operators tab is not on offer at
 * all, so there is nothing for the mode to narrow. */
export const rostersAdminTabsFor = ({
  canAccessOperatorFleet,
  platformMode,
}: Readonly<{
  canAccessOperatorFleet: boolean;
  platformMode: boolean;
}>): readonly RostersAdminTab[] => {
  if (!canAccessOperatorFleet) return ROSTERS_ADMIN_TABS;

  return platformMode
    ? [ROSTERS_ADMIN_TAB.OPERATORS]
    : [ROSTERS_ADMIN_TAB.OPERATORS, ...ROSTERS_ADMIN_TABS];
};

/** The first tab a caller is offered is where they land. */
export const defaultRostersAdminTab = (tabs: readonly RostersAdminTab[]): RostersAdminTab =>
  tabs[0] ?? DEFAULT_ROSTERS_ADMIN_TAB;

/** The write doors the line-item action menus open. */
export const WRITE_DOOR = {
  MINT: 'mint',
  CREATE_COHORT: 'createCohort',
  MANAGE_COACHES: 'manageCoaches',
  MANAGE_MEMBERS: 'manageMembers',
  RENAME_COHORT: 'renameCohort',
  REVOKE: 'revoke',
} as const;

export type WriteDoor = (typeof WRITE_DOOR)[keyof typeof WRITE_DOOR];

export const ROSTERS_ADMIN_PARAM = {
  tab: 'tab',
} as const;

/** Resolved against what the caller is offered, so a tab absent from that list is
 * unreachable by URL as well as by click. */
export const parseRostersAdminTab = (
  value: string | null,
  tabs: readonly RostersAdminTab[],
): RostersAdminTab => tabs.find((tab) => tab === value) ?? defaultRostersAdminTab(tabs);

/**
 * What a roster carrying no kind filters under. A leading space keeps it out of
 * reach of any real kind, so "no kind" stays a choice rather than collapsing
 * into the unfiltered view.
 */
export const NO_KIND = ' none';

export const kindValue = (roster: AdminRoster): string => roster.offering.kind ?? NO_KIND;

/**
 * The institutions the current read carries. Both narrowings are applied to what was
 * read rather than to what is asked for, so choosing one cannot collapse the choice
 * set the next read would offer.
 */
export const rosterInstitutions = (rosters: readonly AdminRoster[]): NamedRef[] => {
  const byId = new Map<number, NamedRef>();

  rosters.forEach(({ managingInstitution }) => {
    if (managingInstitution.id !== null && !byId.has(managingInstitution.id)) {
      byId.set(managingInstitution.id, managingInstitution);
    }
  });

  return Array.from(byId.values());
};

export const matchesInstitution = (
  roster: AdminRoster,
  selectedInstitutionId: number | null,
): boolean =>
  selectedInstitutionId === null || roster.managingInstitution.id === selectedInstitutionId;
